import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';

import { useUser } from '../allauth_auth/hooks.js';

const STORAGE_KEY = 'ahoy_internal_access';

interface CachedAccess {
  userId: number;
  isInternal: boolean;
}

function getCached(userId: number): boolean | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const cached: CachedAccess = JSON.parse(raw);
    if (cached.userId !== userId) {
      return null;
    }
    return cached.isInternal;
  } catch {
    return null;
  }
}

function setCache(userId: number, isInternal: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, isInternal }));
  } catch {
    // localStorage may throw (quota exceeded, private browsing) - ignore silently
  }
}

/**
 * Return type for useIsInternalUser hook.
 * - null: Still loading/revalidating (no cached value exists)
 * - true: User has internal access
 * - false: User does not have internal access
 *
 * Implementation note: this hook MUST be backed by React Query so that
 * multiple consumers on the same page (e.g. SideNavigation and
 * SidebarUserFooter) share a single in-flight request and a single cached
 * result. Previously this hook used a raw `apiGet` inside a `useEffect`,
 * which caused 1 GET per consumer per render — and 4× per page in dev mode
 * because React.StrictMode double-invokes effects. The result was 4
 * identical `/api/internal/access-check/` requests on every proposal page
 * load. Routing through `useQuery` collapses that to a single shared call,
 * matching the canonical-hook pattern adopted in PR #5616 for
 * `/association-types/`.
 */
export function useIsInternalUser(): boolean | null {
  const user = useUser();
  const userId = user?.id;

  const { data } = useQuery({
    queryKey: ['internal-access-check', userId] as const,
    queryFn: async (): Promise<boolean> => {
      try {
        await apiGet('/api/internal/access-check/');
        if (userId !== undefined) {
          setCache(userId, true);
        }
        return true;
      } catch {
        if (userId !== undefined) {
          setCache(userId, false);
        }
        return false;
      }
    },
    enabled: userId !== undefined,
    // Revalidate at most every 5 minutes — internal access doesn't change
    // frequently, but we want to reflect revoked access within a short
    // window without hammering the backend.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    // Seed from localStorage so first render doesn't flicker through
    // null while the network round-trip happens. This preserves the
    // behavior of the previous useEffect-based implementation.
    initialData: () => {
      if (userId === undefined) {
        return undefined;
      }
      const cached = getCached(userId);
      return cached === null ? undefined : cached;
    },
    // Treat localStorage-seeded data as immediately stale so we still
    // revalidate against the backend in the background — matches the
    // previous "always revalidate to reflect revoked access" semantics.
    initialDataUpdatedAt: 0,
  });

  if (userId === undefined) {
    return null;
  }
  return data ?? null;
}
